import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart, BarChart, DoughnutChart } from '../../components/charts';
import { 
  TrendingUp, 
  MousePointerClick, 
  DollarSign, 
  ShoppingCart
} from 'lucide-react';
import { toast } from 'react-hot-toast';

export default function GoogleAdsOverview() {
  const [dateRange, setDateRange] = useState('last30');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Sample data
      const sampleData = {
        overview: {
          spend: 125000,
          impressions: 1500000,
          clicks: 75000,
          conversions: 3000,
          revenue: 450000
        },
        campaigns: [
          {
            id: 1,
            name: "Brand Campaign",
            status: "Active",
            spend: 45000,
            impressions: 500000,
            clicks: 25000,
            ctr: 5,
            avgCpc: 1.8,
            conversions: 1200,
            convRate: 4.8,
            revenue: 180000,
            roas: 4
          },
          {
            id: 2,
            name: "Generic Search",
            status: "Active",
            spend: 35000,
            impressions: 400000,
            clicks: 20000,
            ctr: 5,
            avgCpc: 1.75,
            conversions: 800,
            convRate: 4,
            revenue: 120000,
            roas: 3.43
          },
          {
            id: 3,
            name: "Competitor",
            status: "Active",
            spend: 25000,
            impressions: 300000,
            clicks: 15000,
            ctr: 5,
            avgCpc: 1.67,
            conversions: 600,
            convRate: 4,
            revenue: 90000,
            roas: 3.6
          }
        ]
      };
      
      setData(sampleData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  if (!data) {
    return <div className="p-8">No data available</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Google Ads Overview</h1>
        <div className="flex items-center gap-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl bg-white shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px]"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 Days</option>
            <option value="last30">Last 30 Days</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
          </select>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-5 gap-6">
        <Card className="p-4">
          <h3 className="text-sm text-gray-500">Total Spend</h3>
          <p className="text-xl font-semibold mt-1">₺{data.overview?.spend?.toLocaleString() || '0'}</p>
        </Card>
        <Card className="p-4">
          <h3 className="text-sm text-gray-500">Impressions</h3>
          <p className="text-xl font-semibold mt-1">{data.overview?.impressions?.toLocaleString() || '0'}</p>
        </Card>
        <Card className="p-4">
          <h3 className="text-sm text-gray-500">Clicks</h3>
          <p className="text-xl font-semibold mt-1">{data.overview?.clicks?.toLocaleString() || '0'}</p>
        </Card>
        <Card className="p-4">
          <h3 className="text-sm text-gray-500">Conversions</h3>
          <p className="text-xl font-semibold mt-1">{data.overview?.conversions?.toLocaleString() || '0'}</p>
        </Card>
        <Card className="p-4">
          <h3 className="text-sm text-gray-500">Revenue</h3>
          <p className="text-xl font-semibold mt-1">₺{data.overview?.revenue?.toLocaleString() || '0'}</p>
        </Card>
      </div>

      {/* Campaigns Table */}
      <Card className="overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">Campaigns</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-3 px-4">Campaign</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Spend</th>
                <th className="text-right py-3 px-4">Impressions</th>
                <th className="text-right py-3 px-4">Clicks</th>
                <th className="text-right py-3 px-4">CTR</th>
                <th className="text-right py-3 px-4">Avg. CPC</th>
                <th className="text-right py-3 px-4">Conv.</th>
                <th className="text-right py-3 px-4">Conv. Rate</th>
                <th className="text-right py-3 px-4">Revenue</th>
                <th className="text-right py-3 px-4">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {data.campaigns?.map((campaign) => (
                <tr key={campaign.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">{campaign.name}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      campaign.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {campaign.status}
                    </span>
                  </td>
                  <td className="text-right py-3 px-4">₺{campaign.spend?.toLocaleString() || '0'}</td>
                  <td className="text-right py-3 px-4">{campaign.impressions?.toLocaleString() || '0'}</td>
                  <td className="text-right py-3 px-4">{campaign.clicks?.toLocaleString() || '0'}</td>
                  <td className="text-right py-3 px-4">{campaign.ctr?.toFixed(2) || '0'}%</td>
                  <td className="text-right py-3 px-4">₺{campaign.avgCpc?.toFixed(2) || '0'}</td>
                  <td className="text-right py-3 px-4">{campaign.conversions?.toLocaleString() || '0'}</td>
                  <td className="text-right py-3 px-4">{campaign.convRate?.toFixed(2) || '0'}%</td>
                  <td className="text-right py-3 px-4">₺{campaign.revenue?.toLocaleString() || '0'}</td>
                  <td className="text-right py-3 px-4">{campaign.roas?.toFixed(2) || '0'}x</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function MetricCard({ title, value, change, icon, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          <p className={`text-sm mt-1 ${change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
            {change}
          </p>
        </div>
        <div className={`p-3 rounded-xl ${colors[color]}`}>
          {icon}
        </div>
      </div>
    </Card>
  );
} 